import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, AlertCircle, FileSpreadsheet } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { apiJson } from '@/lib/api';
import API_BASE from '@/lib/apiBase';
import { DistributionConfirmModal } from '@/components/signoff/DistributionConfirmModal';
import { useToast } from '@/hooks/use-toast';
import { SHOW_LEGACY_PDF_ACTIONS } from '@/lib/flags';
import {
  AuthenticatedDownloadError,
  downloadAuthenticatedFile,
} from '@/lib/downloadAuthenticated';

interface PreviewLauncherProps {
  cycleId: number;
  cycleName?: string;
  disabled?: boolean;
  /** Admin-only: sign-off Excel endpoints reject non-admins with 403. */
  showExcelExports?: boolean;
}

export default function PreviewLauncher({
  cycleId,
  cycleName,
  disabled = false,
  showExcelExports = false,
}: PreviewLauncherProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState<'with-removals' | 'postings-only' | null>(null);
  const [error, setError] = useState<string>('');
  const [includeImages, setIncludeImages] = useState(true);
  const [showPdfConfirm, setShowPdfConfirm] = useState(false);
  const { toast } = useToast();

  const handleOpenPreview = async () => {
    if (disabled || isLoading) return;

    try {
      setIsLoading(true);
      setError('');

      import('@/lib/dbg').then(({ dbg }) => dbg.log('[PDF_PREVIEW] Launching preview for cycle', { id: cycleId, name: cycleName }));
      console.log('[PDF_PREVIEW] requesting_ticket', { cycleId });

      const { ticket } = await apiJson<{ ticket: string }>('/api/pdf/ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cycleId }),
      });
      console.log('[PDF_PREVIEW] ticket_received', { cycleId });

      const previewUrl = `${API_BASE}/api/pdf/html/${cycleId}?ticket=${encodeURIComponent(ticket)}&includeImages=${includeImages}`;
      console.log('[POSTING_INSTRUCTIONS_IMAGE_PHASE_2] Opening preview with includeImages:', includeImages);
      const newWindow = window.open(previewUrl, '_blank', 'noopener,noreferrer');

      if (!newWindow) {
        throw new Error('Failed to open new window. Please check your popup blocker.');
      }

      console.log('[PDF_PREVIEW] preview_opened', { cycleId });
    } catch (err: any) {
      console.error('[PDF_PREVIEW] preview_error', { cycleId, error: err.message });
      setError(err.message || 'Failed to open preview');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAllAsSent = async () => {
    try {
      const result = await apiJson<{ success: boolean; updatedCount: number; method: string }>('/api/assignments/mark-sent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          method: 'pdf',
          cycleId,
        }),
      });

      toast({
        title: '✅ All Assignments Marked',
        description: `Marked ${result.updatedCount} assignments as sent to operator (PDF)`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to mark all assignments:', error);
      toast({
        title: '❌ Failed',
        description: (error as Error)?.message || 'Failed to mark assignments',
        variant: 'destructive',
      });
    }
  };

  const handleExcelDownload = async (includeRemovals: boolean) => {
    if (disabled || excelLoading) return;
    const variant = includeRemovals ? 'with-removals' : 'postings-only';
    setExcelLoading(variant);
    setError('');
    try {
      const suffix = includeRemovals ? 'posting_and_removals' : 'posting_only';
      await downloadAuthenticatedFile(
        `/api/signoff/excel/${cycleId}?includeRemovals=${includeRemovals ? 'true' : 'false'}`,
        `signoff_cycle_${cycleId}_${suffix}.xlsx`,
      );
    } catch (err) {
      const message =
        err instanceof AuthenticatedDownloadError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to download Excel export';
      setError(message);
      toast({
        title: 'Excel download failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setExcelLoading(null);
    }
  };

  return (
    <div className="space-y-3">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {SHOW_LEGACY_PDF_ACTIONS && (
        <>
          <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-md border">
            <Checkbox
              id="include-images"
              checked={includeImages}
              onCheckedChange={(checked) => setIncludeImages(checked === true)}
            />
            <label
              htmlFor="include-images"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Include posting instruction images in PDF
            </label>
          </div>

          <Button
            onClick={handleOpenPreview}
            disabled={disabled || isLoading}
            variant="default"
            className="w-full bg-green-600 hover:bg-green-700 text-white"
          >
            <FileText className="h-4 w-4 mr-2" />
            {isLoading ? 'Opening Preview...' : '📄 Open Print Preview (Snapshots)'}
          </Button>

          <Button
            onClick={() => setShowPdfConfirm(true)}
            disabled={disabled || isLoading}
            variant="default"
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
          >
            <FileText className="h-4 w-4 mr-2" />
            {isLoading ? 'Opening Preview...' : '📄 Open Print Preview & Mark All Sent'}
          </Button>

          <DistributionConfirmModal
            open={showPdfConfirm}
            onOpenChange={setShowPdfConfirm}
            method="pdf"
            onConfirm={async () => {
              await handleMarkAllAsSent();
              await handleOpenPreview();
            }}
          />
        </>
      )}

      {showExcelExports && (
        <div className="flex gap-2">
          <Button
            onClick={() => void handleExcelDownload(true)}
            disabled={disabled || excelLoading !== null}
            variant="outline"
            className="flex-1"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            {excelLoading === 'with-removals' ? 'Downloading…' : 'Excel (Postings + Removals)'}
          </Button>
          <Button
            onClick={() => void handleExcelDownload(false)}
            disabled={disabled || excelLoading !== null}
            variant="outline"
            className="flex-1"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            {excelLoading === 'postings-only' ? 'Downloading…' : 'Excel (Postings Only)'}
          </Button>
        </div>
      )}
    </div>
  );
}
